

import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import parse from "html-react-parser";

import {
  useGetCourseBySlugQuery,
  useGetCourseProgressQuery,
} from "@/features/api/courseApi";

import { useGetOrdersQuery } from "@/features/api/paymentApi";

import {
  Clock,
  BarChart,
  Languages,
  ListVideo,
  ChevronRight,
  Video,
  GraduationCap,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";

import {
  Breadcrumb,
  BreadcrumbLink,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

export default function CourseDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth.user);

  /* ---------------- FETCH COURSE ---------------- */
  const { data, isLoading, error, refetch } = useGetCourseBySlugQuery(slug);
  const course = data?.course;

  /* ------------- FETCH USER PROGRESS -------------- */
  const { data: progressData } = useGetCourseProgressQuery(course?._id, {
    skip: !user || !course?._id,
  });

  /* ------------- FETCH ORDERS (PAID) -------------- */
  const { data: ordersData } = useGetOrdersQuery(
    { search: user?.email },
    { skip: !user }
  );

  

  /* ------------- CLEAN MEDIA PATHS -------------- */
  const media = (url) =>
    url?.startsWith("/uploads") ? `https://fetc.in${url}` : url || "";

  /* ---------------- ERROR HANDLING ---------------- */
  useEffect(() => {
    if (error) toast.error("Failed to load course");
  }, [error]);

  /* ----------- AUTO-REFRESH EVERY 30 SEC ---------- */
  useEffect(() => {
    const t = setInterval(() => refetch?.(), 30000);
    return () => clearInterval(t);
  }, [refetch]);

  /* ------------- LOADING UI (BEST) -------------- */
  if (isLoading || !course) {
    return (
      <div className="container px-4 py-12 mx-auto">
        <Skeleton className="h-72 rounded-xl w-full" />
        <Skeleton className="h-6 w-2/5 mt-6" />
        <Skeleton className="h-4 w-1/2 mt-2" />
      </div>
    );
  }

  /* ------------- UNPUBLISHED CHECK -------------- */
  const isCreator = user?._id === course?.creator;
  if (course.status !== "published" && !isCreator) {
    return (
      <div className="container px-4 py-16">
        <Card>
          <CardHeader>
            <CardTitle>Course Unavailable</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              This course is not published yet.
            </p>
            <Button onClick={() => navigate("/")}>Go Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  /* ---------------- ENROLLMENT LOGIC ---------------- */
  const courseId = String(course._id);

const viaProfile =
  user?.enrolledCourses?.some(
    (id) => String(id) === courseId
  );

  const orders =
  ordersData?.data ||
  ordersData?.transactions ||
  ordersData?.orders ||
  [];

const viaOrder = orders.some(
  (o) => String(o.courseId) === courseId && o.status === "SUCCESS"
);

  const isEnrolled = viaProfile || viaOrder;

  /* ---------------- BUTTON TEXT ---------------- */
  let buttonText =
    course.price > 0 ? `Buy Now ₹${course.price}` : "Enroll for Free";

  if (isEnrolled) {
    buttonText =
      progressData?.percentage > 0
        ? "Continue Learning"
        : "Start Course";
  }

  /* ---------------- ENROLL HANDLER ---------------- */
  const handleCTA = () => {
    if (!user) return navigate("/login");

    if (isEnrolled) {
      return navigate(`/courses/${course.slug}/learn`);
    }

    // First time → always redirect to checkout
    const q = new URLSearchParams({
      courseId: course._id,
      slug: course.slug, 
      price: course.price ?? 0,
      courseName: course.title || "",
      courseImage: media(course.featuredImage),
      productType: "course",
    });

    navigate(`/checkout?${q}`);
  };

  /* ---------------- INSTRUCTOR INITIALS ---------------- */
  const instructorInit =
    (course.instructorName || "IN")
      .split(" ")
      .map((s) => s[0])
      .join("")
      .toUpperCase();

  /* ---------------- SAFE HTML ---------------- */
  const cleanHtml = parse(DOMPurify.sanitize(course.content || ""));

  /* ---------------- UI START ---------------- */
  return (
    <div className="bg-gray-50 min-h-screen">
      <Helmet>
        <title>{course.title}</title>
        <meta
          name="description"
          content={course.metaDescription || ""}
        />
      </Helmet>

      <div className="container px-4 py-10 mx-auto">
        {/* ---------------- BREADCRUMB ---------------- */}
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/courses">Courses</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>{course.title}</BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="grid lg:grid-cols-3 gap-10">
          {/* ---------------- LEFT COLUMN ---------------- */}
          <div className="lg:col-span-2 space-y-8">
            {/* -------- TITLE -------- */}
            <h1 className="text-4xl font-bold">{course.title}</h1>
            <p className="text-gray-600">{course.metaDescription}</p>

            {/* -------- Instructor Small Card -------- */}
            <div className="flex items-center gap-4">
              <Avatar>
                {course.instructorImage ? (
                  <AvatarImage src={media(course.instructorImage)} />
                ) : (
                  <AvatarFallback>{instructorInit}</AvatarFallback>
                )}
              </Avatar>
              <div>
                <div className="font-medium">
                  {course.instructorName}
                </div>
                <div className="text-sm text-gray-500">
                  {course.instructorBio}
                </div>
              </div>
            </div>

            {/* -------- MEDIA PREVIEW -------- */}
            <div className="rounded-xl overflow-hidden shadow">
              {course.videoUrl ? (
                <video
                  controls
                  src={media(course.videoUrl)}
                  className="w-full h-[420px] object-cover bg-black"
                />
              ) : (
                <img
                  src={media(course.featuredImage)}
                  alt={course.title}
                  className="w-full h-[420px] object-cover"
                />
              )}
            </div>

            {/* -------- What You Will Learn -------- */}
            <Card>
              <CardHeader>
                <CardTitle>What You Will Learn</CardTitle>
              </CardHeader>
              <CardContent>
                {Array.isArray(course.learningOutcomes) &&
                course.learningOutcomes.length ? (
                  <ul className="list-disc pl-6 space-y-1">
                    {course.learningOutcomes.map((o, i) => (
                      <li key={i}>{o}</li>
                    ))}
                  </ul>
                ) : (
                  <p>No learning outcomes added.</p>
                )}
              </CardContent>
            </Card>

            {/* -------- Lessons List -------- */}
            <Card>
              <CardHeader>
                <CardTitle>Lessons</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
              {(course.lessons || [])
                .filter((l) => !l.isDeleted)
                .map((lesson, idx) => (
                      <div
                    key={lesson._id}
                    className="flex justify-between items-center p-3 border rounded-md hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-4">
                      <span className="h-8 w-8 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center">
                        {idx + 1}
                      </span>
                      <span>{lesson.title}</span>
                    </div>
                    <ChevronRight className="text-gray-400" />
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* -------- About Section -------- */}
            <Card>
              <CardHeader>
                <CardTitle>About This Course</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose max-w-none">{cleanHtml}</div>
              </CardContent>
            </Card>

            {/* -------- FAQs -------- */}
            {course.faqs?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>FAQs</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {course.faqs.map((f, i) => (
                    <div key={i}>
                      <div className="font-semibold">{f.question}</div>
                      <div className="text-sm text-gray-600">
                        {f.answer}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>

          {/* ---------------- RIGHT SIDEBAR ---------------- */}
          <aside className="sticky top-8 space-y-6">
            <Card>
              <CardHeader>
                <div>
                  <div className="text-4xl font-bold">
                    {course.price > 0 ? `₹${course.price}` : "Free"}
                  </div>
                  <p className="text-gray-500 text-sm mt-1">
                    {course.level} • {course.language}
                  </p>
                </div>
              </CardHeader>

              <CardContent>
                <Button
                  size="lg"
                  className="w-full"
                  onClick={handleCTA}
                  disabled={false}                >
                  {buttonText}
                </Button>

                {isEnrolled && (
                  <div className="mt-4">
                    <Progress
                      value={progressData?.percentage || 0}
                      className="h-2"
                    />
                    <p className="text-sm text-gray-500 mt-2">
                      {progressData?.percentage || 0}% completed
                    </p>
                  </div>
                )}

                <Separator className="my-4" />

                {/* ---- Only Sidebar Shows Course Details ---- */}
                <div className="space-y-3 text-sm">

                  <div className="flex justify-between">
                    <span className="flex items-center gap-2">
                      <Clock className="w-4" /> Duration
                    </span>
                    <span>{course.duration || "N/A"}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="flex items-center gap-2">
                      <ListVideo className="w-4" /> Lessons
                    </span>
                    <span>{course.lessons?.length || 0}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="flex items-center gap-2">
                      <GraduationCap className="w-4" /> Certificate
                    </span>
                    <span>{course.certificate ? "Yes" : "No"}</span>
                  </div>

                  <div className="flex justify-between">
                    <span className="flex items-center gap-2">
                      <Languages className="w-4" /> Language
                    </span>
                    <span>{course.language}</span>
                  </div>

                  <div className="flex justify-between">
                    <span>Published</span>
                    <span>
                      {course.publishedAt
                        ? new Date(
                            course.publishedAt
                          ).toLocaleDateString()
                        : "—"}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ---- Instructor Card ---- */}
            <Card>
              <CardHeader>
                <CardTitle>Instructor</CardTitle>
              </CardHeader>
              <CardContent className="flex items-center gap-4">
                <Avatar>
                  {course.instructorImage ? (
                    <AvatarImage src={media(course.instructorImage)} />
                  ) : (
                    <AvatarFallback>{instructorInit}</AvatarFallback>
                  )}
                </Avatar>
                <div>
                  <div className="font-medium">
                    {course.instructorName}
                  </div>
                  <div className="text-sm text-gray-500">
                    {course.instructorBio}
                  </div>
                </div>
              </CardContent>
            </Card>
          </aside>
        </div>
      </div>
    </div>
  );
}
